import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Search, Building2, ClipboardList, Activity, Loader2, ArrowUpRight, Trash2, FileSpreadsheet, FileText, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { hasRole } from "@/lib/roles";
import { ASSESSMENT_TYPE_LABEL } from "@/lib/assessmentTypes";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS_LABEL = {
  // Legacy statuses
  draft: { label: "Bozza", cls: "bg-slate-100 text-slate-700" },
  completed: { label: "Completato", cls: "bg-viva-100 text-viva-700" },
  validated: { label: "Validato", cls: "bg-indigo-100 text-indigo-700" },
  archived: { label: "Archiviato", cls: "bg-amber-100 text-amber-700" },
  // Workflow phases (Disciplinare § 6.5 — Fasi 1..10)
  domanda_inviata: { label: "Domanda inviata", cls: "bg-viva-100 text-viva-800" },
  contratto_firmato: { label: "Contratto firmato", cls: "bg-sky-100 text-sky-800" },
  riesame_domanda: { label: "Riesame domanda", cls: "bg-viva-100 text-viva-800" },
  piano_audit: { label: "Piano di audit", cls: "bg-sky-100 text-sky-800" },
  analisi_documentale: { label: "Trasmissione documentale", cls: "bg-sky-100 text-sky-800" },
  stage1_completato: { label: "Stage 1 chiuso", cls: "bg-indigo-100 text-indigo-800" },
  stage2_completato: { label: "Stage 2 chiuso", cls: "bg-indigo-100 text-indigo-800" },
  rating_elaborato: { label: "Rating elaborato", cls: "bg-purple-100 text-purple-800" },
  riesame_tecnico: { label: "Riesame tecnico", cls: "bg-purple-100 text-purple-800" },
  delibera: { label: "In delibera", cls: "bg-purple-100 text-purple-800" },
  emesso: { label: "Certificato emesso", cls: "bg-emerald-100 text-emerald-800" },
  sospeso: { label: "Sospeso", cls: "bg-amber-100 text-amber-800" },
  revocato: { label: "Revocato", cls: "bg-red-100 text-red-800" },
  scaduto: { label: "Scaduto", cls: "bg-slate-200 text-slate-700" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, o, st] = await Promise.all([api.get("/assessments"), api.get("/organizations"), api.get("/dashboard/stats")]);
      setAssessments(a.data);
      setOrgs(o.data);
      setStats(st.data);
    } catch (e) {
      // 401 is handled globally by the axios interceptor (redirects to /login).
      // For any other error, surface a toast but never crash the dashboard.
      if (e?.response?.status && e.response.status !== 401) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore nel caricamento della dashboard");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function deleteAssessment(a) {
    try {
      await api.delete(`/assessments/${a.id}`);
      toast.success(`Assessment "${a.organization_name || ''} ${a.year || ''}" archiviato`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore archiviazione");
    }
  }

  async function downloadXlsx(a) {
    try {
      const res = await api.get(`/assessments/${a.id}/export-xlsx`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `VIVA-ESG-${a.organization_name || "export"}-${a.year}.xlsx`;
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Excel scaricato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore download Excel");
    }
  }

  async function downloadClientePdf(a) {
    try {
      const res = await api.get(`/assessments/${a.id}/cliente-pdf`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `VIVA-ESG-Riepilogo-${a.organization_name || "questionario"}-${a.year}.pdf`;
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("PDF riepilogo scaricato");
    } catch (e) {
      // Backend may parse the blob into a JSON error → unwrap it for the toast.
      let detail = e.response?.data?.detail;
      if (!detail && e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const json = JSON.parse(txt);
          detail = json.detail;
        } catch (_) { /* ignore */ }
      }
      toast.error(formatApiErrorDetail(detail) || "Errore download PDF");
    }
  }

  const filtered = useMemo(
    () => assessments.filter((a) => (a.organization_name || "").toLowerCase().includes(search.toLowerCase())),
    [assessments, search]
  );

  const kpis = [
    { key: "total", label: "Assessment totali", value: stats?.total ?? 0, icon: ClipboardList, color: "viva" },
    { key: "organizations", label: "Aziende registrate", value: stats?.organizations ?? 0, icon: Building2, color: "indigo" },
    { key: "draft", label: "In bozza", value: stats?.draft ?? 0, icon: Activity, color: "amber" },
    { key: "in-evaluation", label: "In valutazione", value: stats?.in_evaluation ?? 0, icon: Activity, color: "indigo" },
    { key: "issued", label: "Certificati emessi", value: stats?.issued ?? 0, icon: BadgeCheck, color: "viva" },
  ];

  const canCreate = hasRole(user, "admin", "valuatore");
  const isCliente = hasRole(user, "cliente");
  const ownOrg = isCliente ? orgs.find((o) => o.id === user?.organization_id) : null;
  const ownAssessments = isCliente
    ? assessments.filter((a) => a.organization_id === user?.organization_id)
    : assessments;
  const activeAssessment = ownAssessments.find((a) => !["validated", "archived", "emesso", "revocato", "scaduto"].includes(a.status))
    || ownAssessments[0];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
      {/* Cliente-specific welcome banner */}
      {isCliente && ownOrg && (
        <Card
          data-testid="client-welcome-banner"
          className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-viva-500 to-viva-700 text-white border-0 shadow-soft overflow-hidden relative"
        >
          <div className="absolute right-0 top-0 w-40 h-40 bg-white/10 rounded-full -translate-y-12 translate-x-12 blur-2xl" />
          <div className="relative z-10 flex items-center justify-between flex-wrap gap-4">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-viva-100 mb-1.5">
                Portale cliente VIVA ESG
              </div>
              <h1 className="font-heading text-3xl font-bold mb-1 truncate" data-testid="client-org-name">
                {ownOrg.ragione_sociale}
              </h1>
              <p className="text-viva-50 text-sm">
                Benvenut{user?.name?.endsWith("a") ? "a" : "o"} <b>{user?.name}</b>.{" "}
                {ownAssessments.length === 0
                  ? "Nessun assessment ancora attivo per la tua azienda."
                  : activeAssessment && !["validated", "archived"].includes(activeAssessment.status)
                  ? `Hai un assessment ${activeAssessment.profile_level} ${activeAssessment.year} da completare.`
                  : "I tuoi assessment sono in fase di validazione. Per consultare i risultati contatta il team VIVA."}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {activeAssessment && !["validated", "archived"].includes(activeAssessment.status) && (
                <Button
                  data-testid="client-cta-compila"
                  onClick={() => navigate(`/assessment/${activeAssessment.id}/compila`)}
                  className="h-11 px-5 rounded-lg bg-white text-viva-700 hover:bg-viva-50 font-semibold"
                >
                  Continua compilazione
                  <ArrowUpRight className="w-4 h-4 ml-1.5" />
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}

      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-viva-600 mb-2">
            {isCliente ? "I tuoi assessment" : "Dashboard"}
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">
            {isCliente ? "Assessment ESG" : `Ciao, ${user?.name?.split(" ")[0]}.`}
          </h1>
          <p className="text-slate-500 mt-1">
            {isCliente
              ? "Compila gli indicatori e allega le evidenze documentali."
              : "Panoramica degli assessment ESG in piattaforma."}
          </p>
        </div>
        {canCreate && (
          <Button
            onClick={() => navigate("/assessment/nuovo")}
            data-testid="new-assessment-button"
            className="h-11 px-5 rounded-lg bg-viva-500 hover:bg-viva-600 text-white font-semibold"
          >
            <Plus className="w-4 h-4 mr-2" /> Nuovo Assessment
          </Button>
        )}
      </div>

      {/* KPI cards — admin/valuatore/revisore only */}
      {!isCliente && (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mb-8">
        {kpis.map((k) => (
          <Card key={k.label} data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`} className="p-6 rounded-2xl border-slate-100 shadow-soft">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500 mb-2">{k.label}</div>
                <div className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">{k.value}</div>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-${k.color}-50`}>
                <k.icon className={`w-5 h-5 text-${k.color}-600`} strokeWidth={1.75} />
              </div>
            </div>
          </Card>
        ))}
      </div>
      )}

      {/* Assessments list */}
      <Card className="rounded-2xl border-slate-100 shadow-soft overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-heading text-xl font-semibold text-slate-900">Assessment recenti</h2>
            <p className="text-sm text-slate-500">Lista completa degli assessment ESG.</p>
          </div>
          {!isCliente && (
            <div className="relative max-w-xs w-full">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Cerca azienda..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="dashboard-search-input"
                className="pl-9 h-10 rounded-lg border-slate-200"
              />
            </div>
          )}
        </div>
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-viva-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <ClipboardList className="w-10 h-10 mx-auto mb-3 text-slate-300" strokeWidth={1.25} />
            <p className="font-medium text-slate-700">Nessun assessment trovato</p>
            <p className="text-sm">{canCreate ? "Inizia creando un nuovo assessment." : "Non ci sono assessment associati al tuo account."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="assessments-table">
              <thead className="bg-slate-50/50">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-6 py-3">Azienda</th>
                  <th className="px-6 py-3">Tipo</th>
                  <th className="px-6 py-3">Anno</th>
                  <th className="px-6 py-3">Profilo</th>
                  <th className="px-6 py-3">Cluster</th>
                  <th className="px-6 py-3">Stato</th>
                  <th className="px-6 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((a) => {
                  const s = STATUS_LABEL[a.status] || STATUS_LABEL.draft;
                  return (
                    <tr key={a.id} className="hover:bg-slate-50/50 transition" data-testid={`assessment-row-${a.id}`}>
                      <td className="px-6 py-4 font-medium text-slate-900">
                        {a.organization_name || "—"}
                        {a.is_test_effective && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-200 text-slate-600" data-testid={`assessment-test-badge-${a.id}`}>TEST</span>}
                      </td>
                      <td className="px-6 py-4 text-slate-600" data-testid={`assessment-type-${a.id}`}>
                        {ASSESSMENT_TYPE_LABEL[a.assessment_type] || <span className="text-slate-400">Non specificato</span>}
                        {a.reference_certificate_code && <div className="text-[10px] font-mono text-slate-400">rif. {a.reference_certificate_code}</div>}
                      </td>
                      <td className="px-6 py-4 text-slate-600">{a.year}</td>
                      <td className="px-6 py-4 text-slate-600">{a.profile_level}</td>
                      <td className="px-6 py-4 text-slate-600">{a.cluster_code}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${s.cls}`}>{s.label}</span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex gap-2 justify-end">
                          {hasRole(user, "valuatore", "cliente") && (
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`assessment-edit-${a.id}`}
                              onClick={() => navigate(`/assessment/${a.id}/compila`)}
                              className="text-viva-700 hover:bg-viva-50"
                              title={isCliente && a.status && a.status !== "draft" ? "Questionario inviato — sola lettura" : "Compila"}
                            >
                              {isCliente && a.status && a.status !== "draft" ? "Visualizza" : "Compila"}
                            </Button>
                          )}
                          {!isCliente && (
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`assessment-results-${a.id}`}
                              onClick={() => navigate(`/assessment/${a.id}/risultati`)}
                            >
                              Risultati <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
                            </Button>
                          )}
                          {isCliente ? (
                            a.status && a.status !== "draft" ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`assessment-export-pdf-${a.id}`}
                                onClick={() => downloadClientePdf(a)}
                                className="text-emerald-700 hover:bg-emerald-50"
                                title="Scarica il riepilogo PDF del questionario inviato"
                              >
                                <FileText className="w-4 h-4 mr-1" /> PDF
                              </Button>
                            ) : null
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`assessment-export-xlsx-${a.id}`}
                              onClick={() => downloadXlsx(a)}
                              className="text-emerald-700 hover:bg-emerald-50"
                              title="Esporta dati completi assessment in Excel"
                            >
                              <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
                            </Button>
                          )}
                          {hasRole(user, "admin") && (a.status || "draft") === "draft" && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  data-testid={`assessment-delete-${a.id}`}
                                  className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                  title="Archivia assessment (solo bozze)"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Archiviare l'assessment?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Stai per archiviare la bozza <b>{a.organization_name || "—"} · {a.year} · {a.profile_level}</b>.
                                    <br /><br />
                                    L'assessment sparirà dagli elenchi ma nessun dato verrà cancellato: risposte, evidenze e
                                    cronologia restano conservate. L'operazione viene registrata nella cronologia modifiche.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel data-testid={`assessment-delete-cancel-${a.id}`}>Annulla</AlertDialogCancel>
                                  <AlertDialogAction
                                    data-testid={`assessment-delete-confirm-${a.id}`}
                                    onClick={() => deleteAssessment(a)}
                                    className="bg-red-600 hover:bg-red-700 text-white"
                                  >
                                    Archivia
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
